/**
 * Redirect to an external checkout URL (Stripe, etc.) while safely escaping
 * any embedding iframe (Emergent preview, mobile WebViews, dashboard shells).
 *
 * Stripe Checkout sends `X-Frame-Options: DENY`, so a naïve
 * `window.location.href = url` from inside a preview iframe leaves the user
 * on a blank framed page. This helper:
 *
 *   1. Detects if the app is running inside a frame it doesn't own.
 *   2. If so, tries `window.top.location.href = url` (same-origin breakout).
 *   3. Falls back to `window.open(url, '_blank')` for cross-origin frames.
 *   4. Uses plain `window.location.href` when the app owns the top window.
 *
 * Because most callers do an async fetch BEFORE they know the URL (which
 * loses the user-gesture token popup blockers need), also expose
 * `openCheckoutWithPromise` which opens a blank tab synchronously on the
 * click and only fills its URL when the API resolves.
 */
function isInIframe() {
  try {
    return window.top !== window.self;
  } catch {
    // Accessing `window.top` cross-origin throws — we're definitely framed.
    return true;
  }
}

export function redirectToCheckout(url) {
  if (!url) return;
  if (!isInIframe()) {
    window.location.href = url;
    return;
  }
  // Framed: try to break the top window first (works when same-origin).
  try {
    window.top.location.href = url;
    return;
  } catch {
    // Cross-origin frame — open a top-level tab.
  }
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (opened) return;
  // Popup blocked and we can't reach top — last-resort in-frame redirect.
  window.location.href = url;
}

/**
 * Preferred variant for async flows: opens a blank tab immediately on the
 * click (preserving the user-gesture token so popup blockers stay quiet),
 * awaits the checkout URL, then sets the tab's location.
 *
 * Usage:
 *   await openCheckoutWithPromise(async () => {
 *     const { data } = await api.post("/tour-pass/checkout", { origin_url });
 *     return data.url;
 *   });
 */
export async function openCheckoutWithPromise(getUrl) {
  if (!isInIframe()) {
    const url = await getUrl();
    if (url) window.location.href = url;
    return url;
  }
  // Framed: open a placeholder tab synchronously so the user gesture is preserved.
  const tab = window.open("about:blank", "_blank", "noopener,noreferrer");
  try {
    const url = await getUrl();
    if (!url) {
      if (tab) tab.close();
      return null;
    }
    if (tab) {
      tab.location.href = url;
    } else {
      // Popup was blocked — try the top-frame breakout, then a same-frame fallback.
      redirectToCheckout(url);
    }
    return url;
  } catch (err) {
    if (tab) tab.close();
    throw err;
  }
}
